import React, { useState, useMemo, useRef, useEffect } from 'react';
import { X } from 'lucide-react';
import { useLazyCommand } from '@soap/modules';
import { Button } from '../../ui/button';
import { Checkbox } from '../../ui/checkbox';
import { Label } from '../../ui/label';
import { LearningObjectiveDto } from '../InProgressUnitsPanel/types';
import { confirmActionButtonClass } from '../../constants/brandPalette';

interface UnitCompletionDialogProps {
    unitId: string;
    unitTitle: string;
    learningObjectives: LearningObjectiveDto[];
    onClose: () => void;
    onSuccess?: () => void;
}

interface TopicObjectiveGroupProps {
    topicIndex: number;
    topicName: string;
    objectives: Array<{ objective: string; key: string; isCompleted: boolean }>;
    selectedObjectives: Set<string>;
    setSelectedObjectives: React.Dispatch<React.SetStateAction<Set<string>>>;
    isSubmitting: boolean;
}

function TopicObjectiveGroup({
    topicIndex,
    topicName,
    objectives,
    selectedObjectives,
    setSelectedObjectives,
    isSubmitting,
}: TopicObjectiveGroupProps) {
    const topicKeys = useMemo(() => objectives.map((o) => o.key), [objectives]);
    const allSelected =
        topicKeys.length > 0 && topicKeys.every((k) => selectedObjectives.has(k));
    const someSelected = topicKeys.some((k) => selectedObjectives.has(k));

    const selectAllCheckboxRef = useRef<HTMLInputElement>(null);
    useEffect(() => {
        const el = selectAllCheckboxRef.current;
        if (el) {
            el.indeterminate = someSelected && !allSelected;
        }
    }, [someSelected, allSelected]);

    const toggleTopicAll = () => {
        setSelectedObjectives((prev) => {
            const next = new Set(prev);
            if (allSelected) {
                topicKeys.forEach((k) => next.delete(k));
            } else {
                topicKeys.forEach((k) => next.add(k));
            }
            return next;
        });
    };

    const topicSelectAllId = `unit-completion-topic-${topicIndex}-select-all`;

    return (
        <div className="border border-gray-200 rounded-lg overflow-hidden bg-white shadow-sm">
            <div className="flex items-start gap-3 border-b border-gray-200 bg-gradient-to-r from-[#1a5172]/12 via-slate-100 to-slate-50 px-4 py-3.5">
                <Checkbox
                    ref={selectAllCheckboxRef}
                    id={topicSelectAllId}
                    checked={allSelected}
                    onCheckedChange={toggleTopicAll}
                    disabled={isSubmitting}
                    className="mt-1 shrink-0"
                    aria-label={
                        allSelected
                            ? `Uncheck all learning objectives for ${topicName}`
                            : `Check all learning objectives for ${topicName}`
                    }
                />
                <div className="flex-1 min-w-0 pt-0.5">
                    <Label
                        htmlFor={topicSelectAllId}
                        className="cursor-pointer text-base font-bold tracking-tight text-[#1a5172] leading-snug block"
                    >
                        {topicName}
                    </Label>
                </div>
            </div>
            <div className="space-y-2.5 px-4 py-4 bg-white">
                {objectives.map(({ objective, key }) => (
                    <div
                        key={key}
                        className="flex items-start gap-3 rounded-md pl-2 -ml-2 pr-1 py-1 hover:bg-gray-50/80 transition-colors"
                    >
                        <Checkbox
                            id={key}
                            checked={selectedObjectives.has(key)}
                            onCheckedChange={(checked) => {
                                if (checked) {
                                    setSelectedObjectives((prev) => new Set(prev).add(key));
                                } else {
                                    setSelectedObjectives((prev) => {
                                        const newSet = new Set(prev);
                                        newSet.delete(key);
                                        return newSet;
                                    });
                                }
                            }}
                            disabled={isSubmitting}
                            className="mt-1 shrink-0"
                        />
                        <Label
                            htmlFor={key}
                            className="text-sm font-normal text-gray-600 cursor-pointer flex-1 leading-relaxed"
                        >
                            {objective}
                        </Label>
                    </div>
                ))}
            </div>
        </div>
    );
}

export function UnitCompletionDialog({ 
    unitId, 
    unitTitle, 
    learningObjectives,
    onClose, 
    onSuccess 
}: UnitCompletionDialogProps) {
    // Flatten all objectives (both completed and incomplete) with their topic names for easier tracking
    const allObjectives = useMemo(() => {
        const flattened: Array<{ topicName: string; objective: string; key: string; isCompleted: boolean }> = [];
        learningObjectives.forEach(group => {
            const topicName = group.e251_TopicName || 'Unknown Topic';
            
            // Add completed objectives
            group.e251_CompletedObjectives?.forEach(objective => {
                flattened.push({
                    topicName,
                    objective,
                    key: `${topicName}-${objective}`,
                    isCompleted: true
                });
            });
            
            // Add incomplete objectives
            group.e251_IncompleteObjectives?.forEach(objective => {
                flattened.push({
                    topicName,
                    objective,
                    key: `${topicName}-${objective}`,
                    isCompleted: false
                });
            });
        });
        return flattened;
    }, [learningObjectives]);
    
    // Initialize selected objectives with all learning objectives (all checked by default)
    const [selectedObjectives, setSelectedObjectives] = useState<Set<string>>(() => {
        const all = new Set<string>();
        learningObjectives.forEach(group => {
            const topicName = group.e251_TopicName || 'Unknown Topic';
            group.e251_CompletedObjectives?.forEach(objective => {
                all.add(`${topicName}-${objective}`);
            });
            group.e251_IncompleteObjectives?.forEach(objective => {
                all.add(`${topicName}-${objective}`);
            });
        });
        return all;
    });

    const [isSubmitting, setIsSubmitting] = useState(false);
    
    const [sendFinishUnitCommand] = useLazyCommand('C273v1_FinishUnit');

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        
        if (isSubmitting) return;
        
        setIsSubmitting(true);
        
        try {
            // Convert selected keys back to objective strings
            const completedObjectives = allObjectives
                .filter(obj => selectedObjectives.has(obj.key))
                .map(obj => obj.objective);
            
            await sendFinishUnitCommand({
                C273_UnitId: unitId,
                C273_CompletedLearningObjectives: completedObjectives
            });
            
            if (onSuccess) {
                onSuccess();
            } else {
                onClose();
            }
        } catch (error) {
            console.error('Error finishing unit:', error);
            setIsSubmitting(false);
        }
    };

    const handleCancel = () => {
        if (!isSubmitting) {
            onClose();
        }
    };

    // Group objectives by topic for display
    const objectivesByTopic = useMemo(() => {
        const grouped: Record<string, Array<{ objective: string; key: string; isCompleted: boolean }>> = {};
        allObjectives.forEach(obj => {
            if (!grouped[obj.topicName]) {
                grouped[obj.topicName] = [];
            }
            grouped[obj.topicName].push({ 
                objective: obj.objective, 
                key: obj.key,
                isCompleted: obj.isCompleted
            });
        });
        return grouped;
    }, [allObjectives]);

    const hasObjectives = learningObjectives.length > 0 && 
        learningObjectives.some(group => 
            (group.e251_CompletedObjectives && group.e251_CompletedObjectives.length > 0) ||
            (group.e251_IncompleteObjectives && group.e251_IncompleteObjectives.length > 0)
        );

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full mx-4 max-h-[90vh] overflow-hidden flex flex-col">
                <div className="p-6 flex-shrink-0">
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="text-xl font-semibold text-gray-900">
                            Complete Unit
                        </h2>
                        <button
                            onClick={handleCancel}
                            disabled={isSubmitting}
                            className="text-gray-400 hover:text-gray-600 transition-colors"
                        >
                            <X size={20} />
                        </button>
                    </div>
                    
                    <p className="text-sm text-gray-600 mb-6">
                        Select which learning objectives were completed for: <strong>{unitTitle}</strong>
                    </p>
                </div>
                
                <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
                    <div className="flex-1 overflow-y-auto px-6 pb-4">
                        {hasObjectives ? (
                            <div className="space-y-6">
                                {Object.entries(objectivesByTopic).map(([topicName, objectives], topicIndex) => (
                                    <TopicObjectiveGroup
                                        key={`topic-group-${topicIndex}`}
                                        topicIndex={topicIndex}
                                        topicName={topicName}
                                        objectives={objectives}
                                        selectedObjectives={selectedObjectives}
                                        setSelectedObjectives={setSelectedObjectives}
                                        isSubmitting={isSubmitting}
                                    />
                                ))}
                            </div>
                        ) : (
                            <div className="text-center py-8 text-gray-500">
                                <p>No learning objectives available for this unit.</p>
                            </div>
                        )}
                    </div>
                    
                    <div className="flex justify-end gap-3 p-6 border-t border-gray-200 flex-shrink-0">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={handleCancel}
                            disabled={isSubmitting}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            disabled={isSubmitting}
                            className={confirmActionButtonClass}
                        >
                            {isSubmitting ? 'Completing...' : 'Complete Unit'}
                        </Button>
                    </div>
                </form>
            </div>
        </div>
    );
}
